// ============================================
// 敵エンティティの生成ファクトリ
// ============================================

import { CONFIG } from '../../config/game-config';
import { ENEMY_CONFIG, type ArmoredEnemyConfig, type SpeedyEnemyConfig, type BaseEnemyConfig, type BossEnemyConfig } from '../../config/enemy-config';
import { getWave, getLanes, getPlayer } from '../../store/index';
import { getDiffConfig } from '../../utils/helpers';
import { getP5 } from '../../core/p5-instance';
import type { Enemy, EnemyType, BaseEnemy, LaserEnemy, MirrorEnemy } from '../../types/game-types';

/**
 * 敵の生成ファクトリ関数
 */
export function createEnemy(type: EnemyType, lane: number, currentLaserLanes: number[] = []): Enemy | null {
    const p = getP5();
    const cfg = ENEMY_CONFIG[type];

    // ボスは個別の生成ロジック（spawnBoss）があるため、ここでは扱わない
    if (type === 'boss') {
        return null;
    }

    const baseEnemy = createEnemyFromConfig(type, cfg as BaseEnemyConfig, lane);

    switch (type) {
        case 'armored': {
            const armoredCfg = cfg as ArmoredEnemyConfig;
            return {
                ...baseEnemy,
                type: 'armored',
                armor: true,
                armorChance: armoredCfg.armorChance,
                dropBonus: armoredCfg.dropBonus
            };
        }
        case 'speedy': {
            const speedyCfg = cfg as SpeedyEnemyConfig;
            return {
                ...baseEnemy,
                type: 'speedy',
                zigzag: true,
                zigzagTimer: 0,
                zigzagDir: p.random() > 0.5 ? 1 : -1,
                zigzagInterval: speedyCfg.zigzagInterval
            };
        }
        case 'laser':
            return initLaserEnemy(baseEnemy, currentLaserLanes);
        case 'mirror':
            return initMirrorEnemy(baseEnemy);
        case 'tank':
            return { ...baseEnemy, type: 'tank' };
        case 'fast':
            return { ...baseEnemy, type: 'fast' };
        case 'normal':
        default:
            return { ...baseEnemy, type: 'normal' };
    }
}

/**
 * LASER敵の初期化
 */
function initLaserEnemy(baseEnemy: BaseEnemy, currentLaserLanes: number[]): LaserEnemy {
    const p = getP5();
    const lanes = getLanes();

    // LASER敵：既存のLASERと違うレーンに配置
    const availableLanes: number[] = [];
    for (let i = 0; i < CONFIG.laneCount; i++) {
        if (!currentLaserLanes.includes(i)) {
            availableLanes.push(i);
        }
    }

    let laserLane = baseEnemy.lane;
    if (availableLanes.length > 0) {
        laserLane = availableLanes[p.floor(p.random(availableLanes.length))];
    }

    return {
        ...baseEnemy,
        type: 'laser',
        lane: laserLane,
        x: lanes[laserLane],
        laserState: 'moving',
        laserTimer: 0,
        stopY: ENEMY_CONFIG.laser.stopY,
        chargeTime: ENEMY_CONFIG.laser.chargeTime,
        laserDuration: ENEMY_CONFIG.laser.laserDuration
    };
}

/**
 * MIRROR敵の初期化
 */
function initMirrorEnemy(baseEnemy: BaseEnemy): MirrorEnemy | null {
    const lanes = getLanes();
    const player = getPlayer();
    if (!player) {
        return null;
    }

    const mirrorLane = (CONFIG.laneCount - 1) - player.lane;
    return {
        ...baseEnemy,
        type: 'mirror',
        isMirror: true,
        lane: mirrorLane,
        x: lanes[mirrorLane],
        targetLane: mirrorLane,
        laneChangeDelay: ENEMY_CONFIG.mirror.laneChangeDelay,
        laneChangeTimer: 0,
        isChangingLane: false
    };
}

/**
 * 設定から敵を生成（共通の敵生成ロジック、ウェーブによるスケーリング付き）
 */
export function createEnemyFromConfig(_type: EnemyType, cfg: BaseEnemyConfig | BossEnemyConfig, lane: number): BaseEnemy {
    const p = getP5();
    const wave = getWave();
    const lanes = getLanes();

    // ウェーブによるスケーリング計算
    const waveScale = wave - 1;
    const diffCfg = getDiffConfig();
    const hpBonus = (1 + waveScale * CONFIG.enemy.progression.hpBonus) * diffCfg.enemyHpMult;
    const speedBonus = 1 + waveScale * CONFIG.enemy.progression.speedBonus;
    const sizeBonus = 1 + waveScale * CONFIG.enemy.progression.sizeBonus;

    // HPは切り上げ (BossEnemyConfig uses baseHp instead of hp)
    const baseHp = 'hp' in cfg ? cfg.hp : cfg.baseHp;
    const scaledHp = p.ceil(baseHp * hpBonus);

    return {
        x: lanes[lane],
        y: -30,
        lane: lane,
        size: cfg.size * sizeBonus,
        hp: scaledHp,
        maxHp: scaledHp,
        color: p.color(cfg.color[0], cfg.color[1], cfg.color[2]),
        rotation: 0,
        rotSpeed: p.random(-cfg.rotSpeed, cfg.rotSpeed),
        speedMult: cfg.speedMult * speedBonus,
        score: cfg.score
    };
}
